import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CubeIcon,
  FunnelIcon,
} from '@heroicons/react/20/solid'
import {
  proxyProductsApi,
  productsApi,
  type ProxyProductWithRelations,
  type ProductSuggestion,
  pdfImportApi,
} from '../../lib/api'
import { Pagination } from '../../components/ui/Pagination'

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  unresolved: { bg: 'bg-amber-100', text: 'text-amber-800' },
  matched: { bg: 'bg-green-100', text: 'text-green-800' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800' },
  created_in_erp: { bg: 'bg-blue-100', text: 'text-blue-800' },
}

const STATUS_LABELS: Record<string, string> = {
  unresolved: 'Unresolved',
  matched: 'Matched',
  rejected: 'Rejected',
  created_in_erp: 'Created in ERP',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

export function ProxyResolutionPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [statusFilter, setStatusFilter] = useState<string>('unresolved')
  const [resolveModal, setResolveModal] = useState<{
    proxy: ProxyProductWithRelations
    suggestions: ProductSuggestion[]
    loading: boolean
    searchQuery: string
  } | null>(null)
  const [rejectModal, setRejectModal] = useState<{
    proxy: ProxyProductWithRelations
    reason: string
  } | null>(null)

  // Fetch proxy products
  const { data: response, isLoading } = useQuery({
    queryKey: ['proxy-products', { page, limit, status: statusFilter }],
    queryFn: () => proxyProductsApi.list({
      page,
      limit,
      filters: statusFilter ? { status: statusFilter } : undefined,
    }),
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['proxy-products-stats'],
    queryFn: () => proxyProductsApi.getStats(),
  })

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: ({ proxyId, productId, notes }: { proxyId: string; productId: string; notes?: string }) =>
      proxyProductsApi.resolve(proxyId, productId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-products'] })
      queryClient.invalidateQueries({ queryKey: ['proxy-products-stats'] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      setResolveModal(null)
    },
  })

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ proxyId, reason }: { proxyId: string; reason?: string }) =>
      proxyProductsApi.reject(proxyId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-products'] })
      queryClient.invalidateQueries({ queryKey: ['proxy-products-stats'] })
      setRejectModal(null)
    },
  })

  // Unresolve mutation
  const unresolveMutation = useMutation({
    mutationFn: (proxyId: string) => proxyProductsApi.unresolve(proxyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-products'] })
      queryClient.invalidateQueries({ queryKey: ['proxy-products-stats'] })
    },
  })

  // Open resolve modal and fetch suggestions
  const openResolveModal = async (proxy: ProxyProductWithRelations) => {
    setResolveModal({
      proxy,
      suggestions: [],
      loading: true,
      searchQuery: proxy.skuGuess || proxy.descriptionExtracted || '',
    })

    try {
      const suggestions = await pdfImportApi.getProductSuggestions(
        proxy.skuGuess,
        proxy.descriptionExtracted || '',
        15
      )
      setResolveModal(prev => prev ? { ...prev, suggestions, loading: false } : null)
    } catch (err) {
      setResolveModal(prev => prev ? { ...prev, loading: false } : null)
    }
  }

  // Search products in modal
  const searchProducts = async () => {
    if (!resolveModal) return

    setResolveModal(prev => prev ? { ...prev, loading: true } : null)

    try {
      const suggestions = await pdfImportApi.getProductSuggestions(
        null,
        resolveModal.searchQuery,
        15
      )
      setResolveModal(prev => prev ? { ...prev, suggestions, loading: false } : null)
    } catch (err) {
      setResolveModal(prev => prev ? { ...prev, loading: false } : null)
    }
  }

  const proxies = response?.data || []
  const total = response?.total || 0
  const totalPages = response?.totalPages || 1

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Proxy Products</h1>
          <p className="mt-1 text-sm text-gray-500">
            Resolve unmatched products from PDF imports
          </p>
        </div>
        <Link
          to="/cpq/pdf-import"
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Import PDF
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
            <p className="text-sm text-gray-500">Unresolved</p>
            <p className="text-2xl font-semibold text-amber-600">{stats.unresolved}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-500">Matched</p>
            <p className="text-2xl font-semibold text-green-600">{stats.matched}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <p className="text-sm text-gray-500">Rejected</p>
            <p className="text-2xl font-semibold text-red-600">{stats.rejected}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center space-x-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <label className="text-sm font-medium text-gray-700">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">All</option>
            <option value="unresolved">Unresolved</option>
            <option value="matched">Matched</option>
            <option value="rejected">Rejected</option>
            <option value="created_in_erp">Created in ERP</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading...</p>
          </div>
        ) : proxies.length === 0 ? (
          <div className="text-center py-12">
            <CubeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <p className="mt-4 text-sm text-gray-500">No proxy products found</p>
            {statusFilter === 'unresolved' && (
              <p className="text-sm text-green-600 mt-2">All products have been resolved!</p>
            )}
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Extracted Data
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolution
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {proxies.map((proxy) => {
                  const statusColor = STATUS_COLORS[proxy.status] || STATUS_COLORS.unresolved

                  return (
                    <tr key={proxy.id} className={proxy.status === 'unresolved' ? 'bg-amber-50' : ''}>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {proxy.skuGuess || 'No SKU'}
                        </div>
                        <div className="text-sm text-gray-500 max-w-xs">
                          {proxy.descriptionExtracted || 'No description'}
                        </div>
                        {proxy.extractedPrice && (
                          <div className="text-xs text-gray-400 mt-1">
                            Price: {proxy.extractedCurrency} {proxy.extractedPrice}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {proxy.rawData?.source === 'pdf' ? (
                            <span className="inline-flex items-center">
                              <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-1" />
                              PDF Import
                            </span>
                          ) : (
                            proxy.rawData?.source || 'Unknown'
                          )}
                        </div>
                        {proxy.sourceQuoteId && (
                          <Link
                            to={`/cpq/quotes/${proxy.sourceQuoteId}`}
                            className="text-xs text-indigo-600 hover:text-indigo-800"
                          >
                            View Quote
                          </Link>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {formatDate(proxy.createdAt as unknown as string)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                          {STATUS_LABELS[proxy.status]}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {proxy.resolvedToProduct ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {proxy.resolvedToProduct.itemCode}
                            </div>
                            <div className="text-sm text-gray-500">
                              {proxy.resolvedToProduct.itemName}
                            </div>
                            {proxy.resolvedBy && (
                              <div className="text-xs text-gray-400 mt-1">
                                by {proxy.resolvedBy.firstName} {proxy.resolvedBy.lastName}
                              </div>
                            )}
                          </div>
                        ) : proxy.resolutionNotes ? (
                          <div className="text-sm text-gray-500">{proxy.resolutionNotes}</div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {proxy.status === 'unresolved' && (
                            <>
                              <button
                                onClick={() => openResolveModal(proxy)}
                                className="text-indigo-600 hover:text-indigo-800"
                                title="Resolve to product"
                              >
                                <MagnifyingGlassIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => setRejectModal({ proxy, reason: '' })}
                                className="text-red-600 hover:text-red-800"
                                title="Reject"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}
                          {(proxy.status === 'matched' || proxy.status === 'rejected') && (
                            <button
                              onClick={() => unresolveMutation.mutate(proxy.id)}
                              disabled={unresolveMutation.isPending}
                              className="text-gray-600 hover:text-gray-800"
                              title="Reset to unresolved"
                            >
                              <ArrowPathIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  limit={limit}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Resolve Modal */}
      {resolveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setResolveModal(null)}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-gray-900 mb-2">Resolve Proxy Product</h3>
              <div className="bg-gray-50 rounded p-3 mb-4">
                <p className="text-sm font-medium">{resolveModal.proxy.skuGuess || 'No SKU'}</p>
                <p className="text-sm text-gray-600">{resolveModal.proxy.descriptionExtracted}</p>
              </div>

              {/* Search */}
              <div className="flex mb-4">
                <input
                  type="text"
                  value={resolveModal.searchQuery}
                  onChange={(e) => setResolveModal(prev => prev ? { ...prev, searchQuery: e.target.value } : null)}
                  onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
                  placeholder="Search products..."
                  className="flex-1 rounded-l-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <button
                  onClick={searchProducts}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Suggestions */}
              {resolveModal.loading ? (
                <div className="text-center py-8">
                  <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">Searching products...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {resolveModal.suggestions.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => resolveMutation.mutate({
                        proxyId: resolveModal.proxy.id,
                        productId: product.id,
                      })}
                      disabled={resolveMutation.isPending}
                      className="w-full text-left px-3 py-2 rounded border border-gray-200 hover:border-indigo-500 hover:bg-indigo-50 disabled:opacity-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{product.itemCode}</p>
                          <p className="text-sm text-gray-600">{product.itemName}</p>
                        </div>
                        <span className="text-xs text-gray-500">{product.confidence}% match</span>
                      </div>
                    </button>
                  ))}
                  {resolveModal.suggestions.length === 0 && (
                    <p className="text-center py-8 text-gray-500">No matching products found</p>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setResolveModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setRejectModal(null)}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Proxy Product</h3>
              <div className="bg-gray-50 rounded p-3 mb-4">
                <p className="text-sm font-medium">{rejectModal.proxy.skuGuess || 'No SKU'}</p>
                <p className="text-sm text-gray-600">{rejectModal.proxy.descriptionExtracted}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={rejectModal.reason}
                  onChange={(e) => setRejectModal(prev => prev ? { ...prev, reason: e.target.value } : null)}
                  placeholder="Why is this proxy being rejected?"
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setRejectModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => rejectMutation.mutate({
                    proxyId: rejectModal.proxy.id,
                    reason: rejectModal.reason || undefined,
                  })}
                  disabled={rejectMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectMutation.isPending ? (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 mr-2" />
                  )}
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
